import type { OthersRow, WaitingOnOthers } from '@myrewards/contracts';
import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import {
  Button,
  ButtonLink,
  Card,
  Icon,
  StatusPill,
  WaitingRow,
  type IconName,
  type WaitingItem,
} from '../ui';
import {
  ALL_OF_THEM_HREF,
  othersIntro,
  othersNothingWords,
  othersOnHome,
  switchedOffWords,
  waitingDays,
} from './words';
import h from './HomeCards.module.css';

// APP-148 D2: a Stock Admin's Home, under Waiting on you. The Executives' decisions and the Sector
// Leads' work, oldest first, as they see it, each row saying whose it is (the Home board, R56).
// Never counted in the bell or in Waiting on you; with both switches off it isn't drawn at all.

const ICON: Readonly<Record<OthersRow['decision'], IconName>> = {
  over_budget: 'alert',
  paa_taa: 'award',
  executive_award: 'award',
  spot_funding: 'award',
  executive_delegate_entry: 'people',
  lead_delegate_entry: 'people',
  lead_returned: 'arrowLeft',
};

/**
 * One row in Home's form (R56): the server's heading and line, how long it has waited (R38,
 * counted to Home's day) before any closing sentence, whose it is, and Open. An over-budget row
 * says so in a pill above its heading.
 */
export function othersItem(row: OthersRow, asOf: string): WaitingItem {
  const line = `${row.detail} · ${waitingDays(row.since, asOf)}`;
  return {
    id: row.key,
    role: row.status,
    roleTone: 'neutral',
    kind: 'task',
    title: row.title,
    detail: row.detailEnd === null ? line : `${line}. ${row.detailEnd}`,
    since: row.since,
    action: { label: 'Open', href: row.href },
    icon: ICON[row.decision],
    ...(row.overPools.length > 0 && { problem: 'Over budget' }),
  };
}

export function WaitingOnOthersCard({
  others,
  asOf,
}: {
  /** What's waiting, or null when it couldn't be read just now. */
  others: WaitingOnOthers | null;
  /** Home's day, for how long each row has waited. */
  asOf: string;
}): ReactElement {
  // The rows the switches leave on, oldest first (as the server orders them, R38).
  const rows = others === null ? [] : othersOnHome(others);
  const count = rows.length;
  const [open, setOpen] = useState(true);
  const bodyId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  // After Hide or Show the pressed button is replaced: focus stays on its counterpart.
  const moved = useRef(false);
  useEffect(() => {
    if (!moved.current) return;
    moved.current = false;
    toggleRef.current?.focus();
  }, [open]);
  return (
    <Card
      title="Waiting on others"
      accent="cyan"
      helpAnchor="waiting-on-others"
      help={{
        shows:
          'The Executives’ decisions and the Sector Leads’ work, oldest first, as they see it: over-budget awards, PAAs and TAAs, Spot funding, delegates’ entries and awards returned to a Sector Lead. Each row says whose it is.',
        canDo:
          'When an Executive or a Sector Lead asks you to, open their row and do it for them. All of them opens Approvals with every section. None of this counts in your bell or in Waiting on you.',
      }}
      {...(count > 0 && { badge: <StatusPill tone="info" label={`${count} waiting`} /> })}
      aside={
        <span className={h.aside}>
          <ButtonLink href={ALL_OF_THEM_HREF} variant="ghost" size="sm">
            All of them
          </ButtonLink>
          {count > 0 && (
            <Button
              ref={toggleRef}
              variant="ghost"
              size="sm"
              aria-expanded={open}
              aria-controls={bodyId}
              onPress={() => {
                moved.current = true;
                setOpen(!open);
              }}
            >
              {open ? 'Hide' : 'Show'}
              <Icon name={open ? 'chevronUp' : 'chevronDown'} size={16} />
            </Button>
          )}
        </span>
      }
    >
      {others === null ? (
        // Like Home's other cards: it says it couldn't read, and where the list is.
        <p className={h.intro} role="alert">
          We couldn’t read what’s waiting on the Executives and Sector Leads just now. All of them
          are on Approvals.
        </p>
      ) : count === 0 ? (
        switchedOffWords(others.switches) === null ? (
          // The Empty board: one line, as Waiting on you says it has nothing.
          <p className={h.nothing} data-variant="caught-up">
            <span className={h.nothingIcon}>
              <Icon name="check" size={20} strokeWidth={2.4} />
            </span>
            {othersNothingWords(others.switches).line}
          </p>
        ) : (
          // R149: a switch off isn't caught up. The line says what isn't listed, with a neutral
          // information mark, never the done check.
          <p className={h.nothing} data-variant="switched-off">
            <span className={h.offIcon}>
              <Icon name="info" size={20} />
            </span>
            {othersNothingWords(others.switches).line}
          </p>
        )
      ) : (
        <div id={bodyId} hidden={!open}>
          <p className={h.intro}>
            {othersIntro(others.switches)} Each row says whose it is. When one of them asks you to,
            open it and do it for them.
          </p>
          <ul className={h.list}>
            {rows.map((row) => (
              <WaitingRow key={row.key} item={othersItem(row, asOf)} />
            ))}
          </ul>
          <p className={h.foot}>
            Not counted in your bell or in “waiting on you”. The Executives and Sector Leads hear
            about these as they do today.
          </p>
        </div>
      )}
    </Card>
  );
}
